import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Box, Container, Typography, Card, CardActionArea,
  InputAdornment, TextField, Chip, Button,
} from '@mui/material';
import {
  Search as SearchIcon,
  Category as CategoryIcon,
  AccountTree as SubCatIcon,
  Storefront as MarcasIcon,
  MenuBook as CatalogoIcon,
  Pets as EspeciesIcon,
  Diversity3 as RazasIcon,
  MedicalServices as AlergiasIcon,
  Store as SucursalesIcon,
  AdminPanelSettings as RolesIcon,
  People as UsuariosIcon,
  MeetingRoom as SalasIcon,
  TravelExplore as ZonasIcon,
  LocationSearching as SubZonasIcon,
  LocalHospital as ConsultoriosIcon,
  Image as BannerIcon,
  DevicesOther as DispositivosIcon,
  QrCode2 as QrIcon,
  ManageAccounts as PerfilIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import Sidebar from './Sidebar';

import { usePermissions } from '../hooks/usePermissions';

// ─── Colores base ────────────────────────────────────
const drawerWidth = 240;
const PRIMARY_DARK_BG = '#0f172a';

// ─── Grupos de configuraciones ───────────────────────────────────────────────
const GRUPOS = [
  {
    titulo: 'Catálogo de Productos',
    color: '#4f46e5',
    items: [
      { label: 'Categorias',    ruta: '/categorias',    Icono: CategoryIcon },
      { label: 'SubCategorias', ruta: '/subcategorias', Icono: SubCatIcon },
      { label: 'Marcas',        ruta: '/marcas',        Icono: MarcasIcon },
      { label: 'Catálogo',      ruta: '/catalogo',      Icono: CatalogoIcon },
    ],
  },
  {
    titulo: 'Pacientes & Clínica',
    color: '#0891b2',
    items: [
      { label: 'Especies',  ruta: '/especies', Icono: EspeciesIcon },
      { label: 'Razas',     ruta: '/razas',    Icono: RazasIcon },
      { label: 'Alergias',  ruta: '/alergias', Icono: AlergiasIcon },
    ],
  },
  {
    titulo: 'Organización & Ubicaciones',
    color: '#059669',
    items: [
      { label: 'Sucursales',       ruta: '/sucursales',   Icono: SucursalesIcon },
      { label: 'Salas Ubicaciones',ruta: '/salas',        Icono: SalasIcon },
      { label: 'Zonas',            ruta: '/zonas',        Icono: ZonasIcon },
      { label: 'Sub-Zonas',        ruta: '/sub-zonas',    Icono: SubZonasIcon },
      { label: 'Consultorios',     ruta: '/consultorios', Icono: ConsultoriosIcon },
    ],
  },
  {
    titulo: 'Acceso & Usuarios',
    color: '#d97706',
    items: [
      { label: 'Roles',    ruta: '/roles',    Icono: RolesIcon },
      { label: 'Usuarios', ruta: '/usuarios', Icono: UsuariosIcon },
      { label: 'Perfil',   ruta: '/petperfil',Icono: PerfilIcon },
    ],
  },
  {
    titulo: 'Sistema & Dispositivos',
    color: '#7c3aed',
    items: [
      { label: 'Banner Receta',      ruta: '/configuracion/banners',    Icono: BannerIcon },
      { label: 'Dispositivos Conf.', ruta: '/dispositivos-confianza',   Icono: DispositivosIcon },
      { label: 'Transferir datos QR',ruta: '/generar-datajs',           Icono: QrIcon },
    ],
  },
];

// ─── Tarjeta individual ───────────────────────────────────────────────────────
const ConfigCard = ({ label, ruta, Icono, accentColor }) => (
  <Card
    sx={{
      borderRadius: 3,
      boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
      transition: 'transform 0.18s ease, box-shadow 0.18s ease',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: `0 8px 24px ${accentColor}30`,
      },
    }}
  >
    <CardActionArea component={Link} to={ruta} sx={{ p: 2.5, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, minHeight: 110 }}>
      <Box sx={{ bgcolor: `${accentColor}15`, borderRadius: 2, p: 1.2, display: 'flex' }}>
        <Icono sx={{ fontSize: 28, color: accentColor }} />
      </Box>
      <Typography variant="body2" sx={{ fontWeight: 600, color: PRIMARY_DARK_BG, textAlign: 'center', lineHeight: 1.3 }}>
        {label}
      </Typography>
    </CardActionArea>
  </Card>
);

// ─── Componente principal ─────────────────────────────────────────────────────
const Configuraciones = ({ openSidebar, setOpenSidebar }) => {
  const navigate = useNavigate();
  const { canAccessConfiguraciones, user } = usePermissions();
  const [busqueda, setBusqueda] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (user && user.id_rol) {
      setIsLoading(false);
      if (!canAccessConfiguraciones()) {
        navigate('/clientes', { replace: true });
      }
    }
  }, [user, canAccessConfiguraciones, navigate]);

  // Loading mientras se carga el usuario
  if (isLoading || !user || !user.id_rol) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <Typography>Cargando permisos...</Typography>
      </Box>
    );
  }

  // Bloqueo de acceso
  if (!canAccessConfiguraciones()) {
    return null;
  }

  // Filtrar items por búsqueda
  const gruposFiltrados = GRUPOS.map(g => ({
    ...g,
    items: g.items.filter(i => i.label.toLowerCase().includes(busqueda.toLowerCase())),
  })).filter(g => g.items.length > 0);

  return (
    <Box sx={{ display: 'flex', bgcolor: '#f1f5f9', minHeight: '100vh' }}>
      <Sidebar openSidebar={openSidebar} setOpenSidebar={setOpenSidebar} />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${openSidebar ? drawerWidth : 0}px)` },
          transition: '0.3s',
        }}
      >
        <Container maxWidth="xl" sx={{ pt: 10 }}>

          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/clientes')} sx={{ mr: 2 }}>
              Regresar
            </Button>
            <Typography variant="h4" sx={{ fontWeight: 800, color: PRIMARY_DARK_BG }}>
              Configuraciones
            </Typography>
          </Box>

          <TextField
            fullWidth
            placeholder="Buscar opción de configuración..."
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            sx={{ mb: 5, bgcolor: 'white', borderRadius: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#94a3b8' }} />
                </InputAdornment>
              ),
            }}
          />

          {gruposFiltrados.map((grupo) => (
            <Box key={grupo.titulo} sx={{ mb: 5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <Box sx={{ width: 4, height: 22, bgcolor: grupo.color, borderRadius: 2 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: PRIMARY_DARK_BG }}>
                  {grupo.titulo}
                </Typography>
                <Chip
                  label={grupo.items.length}
                  size="small"
                  sx={{ bgcolor: `${grupo.color}15`, color: grupo.color, fontWeight: 700, height: 20 }}
                />
              </Box>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: 'repeat(2, 1fr)',
                    sm: 'repeat(3, 1fr)',
                    md: 'repeat(4, 1fr)',
                    lg: 'repeat(5, 1fr)',
                  },
                  gap: 2,
                }}
              >
                {grupo.items.map((item) => (
                  <ConfigCard
                    key={item.ruta}
                    label={item.label}
                    ruta={item.ruta}
                    Icono={item.Icono}
                    accentColor={grupo.color}
                  />
                ))}
              </Box>
            </Box>
          ))}

          {gruposFiltrados.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 10 }}>
              <SearchIcon sx={{ fontSize: 48, color: '#cbd5e1', mb: 2 }} />
              <Typography variant="h6" color="textSecondary">
                No se encontraron opciones para "{busqueda}"
              </Typography>
            </Box>
          )}

        </Container>
      </Box>
    </Box>
  );
};

export default Configuraciones;